import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

const BRONZE = "#A89880";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch (err) { console.warn("Payment status poll failed, retrying", err); }
      if (tries.current >= 12) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#141311" }} data-testid="payment-success-page">
      <div className="max-w-md w-full p-10 text-center" style={{ border: "1px solid rgba(168,152,128,0.35)" }}>
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 size={32} className="mx-auto animate-spin" style={{ color: BRONZE }} />
            <h1 className="lp-h text-3xl mt-6" style={{ color: "#F5F1EA" }}>Confirming your payment…</h1>
            <p className="text-sm mt-3" style={{ color: "#B5AC9E" }}>One moment — we're setting up your membership.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-paid">
            <CheckCircle2 size={36} className="mx-auto" style={{ color: BRONZE }} />
            <h1 className="lp-h text-4xl mt-6" style={{ color: "#F5F1EA" }}>Welcome to FENYX.</h1>
            <p className="text-sm mt-4 leading-relaxed" style={{ color: "#B5AC9E" }}>
              Your membership is active and your account is ready. Log in with the email and
              password you just chose — your programs, recipes, and cycle tools are waiting.
            </p>
            <Link to="/login" data-testid="payment-login-link"
              className="inline-block mt-7 px-10 py-3.5 lp-label-sm transition-opacity hover:opacity-85"
              style={{ background: BRONZE, color: "#141311" }}>Log in to FENYX</Link>
          </div>
        )}
        {state === "pending" && (
          <div data-testid="payment-pending">
            <Loader2 size={32} className="mx-auto animate-spin" style={{ color: BRONZE }} />
            <h1 className="lp-h text-3xl mt-6" style={{ color: "#F5F1EA" }}>Almost there…</h1>
            <p className="text-sm mt-3" style={{ color: "#B5AC9E" }}>
              Your payment is processing. Check your email in a few minutes for your welcome
              note — then log in.
            </p>
          </div>
        )}
        {state === "error" && (
          <div data-testid="payment-error">
            <AlertCircle size={32} className="mx-auto" style={{ color: BRONZE }} />
            <h1 className="lp-h text-3xl mt-6" style={{ color: "#F5F1EA" }}>Something didn't go through.</h1>
            <p className="text-sm mt-3" style={{ color: "#B5AC9E" }}>Your card wasn't charged — or the session expired. You can try again.</p>
            <Link to="/fenyx" className="inline-block mt-6 underline lp-label-sm" style={{ color: BRONZE }}>Back to FENYX</Link>
          </div>
        )}
      </div>
    </div>
  );
}
